import type { InventoryBundle, InventoryDomain } from '@openx/types/gqlTypes';
import type { MetricKey } from './types';

const sum = (numbers: number[]) => numbers.reduce((a, b) => a + b, 0);
const average = (numbers: number[]) => (numbers.length ? sum(numbers) / numbers.length : 0);
const median = (numbers: number[]) => {
  if (!numbers.length) return 0;
  const sorted = numbers.sort((a, b) => a - b);
  const mid = sorted.length >> 1;
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
};

const metricsMethodsConfig: Record<MetricKey, (values: number[]) => number> = Object.freeze({
  totalCprMarketRequests: sum,
  totalImpressions: sum,
  totalBidSpendInUsd: sum,
  avgViewability: average,
  avgOxWinRate: average,
  avgPubWinRate: average,
  avgBidCpm: average,
  relevance: median,
});

type MetricsDeviationResult = {
  allMetrics: Record<MetricKey, number>;
  selectedMetrics: Record<MetricKey, number>;
  deviations: Record<MetricKey, number>;
};

type CalculateMetricsDeviation = (
  items: InventoryDomain[] | InventoryBundle[],
  selectedItems: InventoryDomain[] | InventoryBundle[]
) => MetricsDeviationResult | null;

export const calculateMetricsDeviation: CalculateMetricsDeviation = (items, selectedItems) => {
  if (!selectedItems.length || !items.length) return null;

  const allMetrics: Record<MetricKey, number> = {} as Record<MetricKey, number>;
  const selectedMetrics: Record<MetricKey, number> = {} as Record<MetricKey, number>;
  const deviations: Record<MetricKey, number> = {} as Record<MetricKey, number>;

  for (const key of Object.keys(metricsMethodsConfig) as MetricKey[]) {
    const allValues = items.map(item => Number(item[key]));
    const selectedValues = selectedItems.map(item => Number(item[key]));

    const allAgg = metricsMethodsConfig[key](allValues);
    const selectedAgg = metricsMethodsConfig[key](selectedValues);

    allMetrics[key] = +allAgg.toFixed(10);
    selectedMetrics[key] = +selectedAgg.toFixed(10);

    const deviation = allAgg === 0 ? 0 : ((selectedAgg - allAgg) / allAgg) * 100;
    deviations[key] = +deviation.toFixed(10);
  }

  return { allMetrics, selectedMetrics, deviations };
};
